import React, { useEffect, useMemo, useState } from "react";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Autocomplete,
  Chip,
  Paper,
  Box,
  Typography,
  InputAdornment,
  Tabs,
  Tab,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import muiTheme from "../theme/muiTheme";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";
import SocietarioPosicao from "./SocietarioPosicao";
import SocietarioMovimentos from "./SocietarioMovimentos";
import SocietarioDocumentos from "./SocietarioDocumentos";
import SocietarioFluxo from "./SocietarioFluxo";
import { alteracoesDaEmpresa, validarAlteracao } from "../utils/societarioCalculos";
import { estiloBotaoAcao, estiloCabecalho, estiloCampo, estiloSelect } from "../utils/societarioEstilos";

// ==========================================================
// ALTERAÇÕES CONTRATUAIS - PROCESSOS (seções 3, 4 e 7)
// Gravação no banco via /api/alteracoes-contratuais/processos.
// O servidor repete todas as validações da seção 4.
// ==========================================================

const API = "/api";
const API_PROCESSOS = `${API}/alteracoes-contratuais/processos`;

class ErroApi extends Error {
  constructor(mensagem, detalhes) {
    super(mensagem);
    this.detalhes = detalhes || [];
  }
}

async function chamarApi(caminho = "", opcoes = {}) {
  const tk = localStorage.getItem("token");
  const ehArquivo = opcoes.body instanceof FormData;
  const resposta = await fetch(`${API_PROCESSOS}${caminho}`, {
    ...opcoes,
    headers: { ...(ehArquivo ? {} : { "Content-Type": "application/json" }), Authorization: `Bearer ${tk}` },
  });
  const dados = await resposta.json().catch(() => ({}));
  if (!resposta.ok) throw new ErroApi(dados.erro || "Não foi possível concluir a operação.", dados.detalhes);
  return dados;
}

async function buscarLista(caminho) {
  const tk = localStorage.getItem("token");
  const resposta = await fetch(`${API}${caminho}`, { headers: { Authorization: `Bearer ${tk}` } });
  const dados = await resposta.json().catch(() => []);
  if (!resposta.ok) throw new Error(dados.erro || "Não foi possível carregar os cadastros.");
  return Array.isArray(dados) ? dados : [];
}

const formVazio = {
  empresaId: "",
  numero: "",
  identificacao: "",
  dataAssinatura: "",
  dataRegistroJunta: "",
  responsavelId: null,
  observacoes: "",
  lancamentoHistorico: false,
  posicaoInicial: [],
  movimentos: [],
  documentos: [],
};

const normalizar = (texto) =>
  String(texto ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

const maiusculas = (v) => String(v ?? "").toLocaleUpperCase("pt-BR");
const data = (v) => (v ? new Date(`${v}T00:00:00`).toLocaleDateString("pt-BR") : "—");
const dataHora = (v) => (v ? new Date(v).toLocaleString("pt-BR") : "—");

export default function SocietarioAlteracoes() {
  const [empresas, setEmpresas] = useState([]);
  const [socios, setSocios] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);

  const [alteracoes, setAlteracoes] = useState([]);
  const [processando, setProcessando] = useState(false);

  const [inputFiltro, setInputFiltro] = useState("");
  const [filtro, setFiltro] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(10);
  const [gridMinimizado, setGridMinimizado] = useState(false);

  const [editandoId, setEditandoId] = useState(null);
  const [somenteLeitura, setSomenteLeitura] = useState(false);
  const [form, setForm] = useState({ ...formVazio });
  const [abaAtiva, setAbaAtiva] = useState("geral");
  const [errosValidacao, setErrosValidacao] = useState([]);

  const [confirmDelete, setConfirmDelete] = useState({ aberto: false, id: null });
  const [snack, setSnack] = useState({ aberto: false, msg: "", severity: "success" });
  const mostrarSnack = (msg, severity = "success") => setSnack({ aberto: true, msg, severity });

  // permissões (níveis do sistema) e usuário logado
  const token = localStorage.getItem("token");
  let tipoUsuario = "";
  let usuarioLogadoId = null;
  if (token) {
    try {
      const decoded = jwtDecode(token);
      tipoUsuario = normalizar(decoded.tipo);
      usuarioLogadoId = decoded.id ?? null;
    } catch (_) {}
  }
  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["administrador"].includes(tipoUsuario);

  useEffect(() => {
    if (!podeVisualizar) return;
    Promise.all([
      buscarLista("/alteracoes-contratuais/empresas"),
      buscarLista("/alteracoes-contratuais/socios"),
      buscarLista("/alteracoes-contratuais/etapas"),
      buscarLista("/alcadas/usuarios"),
      buscarLista("/alteracoes-contratuais/processos"),
    ])
      .then(([e, s, et, u, alt]) => {
        setEmpresas(e);
        setSocios(s);
        setEtapas(et);
        setUsuarios(u);
        setAlteracoes(alt);
      })
      .catch((erro) => mostrarSnack(erro.message, "error"))
      .finally(() => setCarregando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeVisualizar]);

  // ── Pesquisa automática
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltro(inputFiltro);
      setPagina(0);
    }, 400);
    return () => clearTimeout(t);
  }, [inputFiltro]);

  const etapasAtivas = useMemo(() => etapas.filter((e) => e.ativo).sort((a, b) => a.ordem - b.ordem), [etapas]);
  const nomeEtapa = (id) => etapas.find((e) => e.id === id)?.nome || "—";
  const nomeEmpresa = (id) => empresas.find((e) => String(e.id) === String(id))?.apelido || "—";
  const nomeUsuario = (id) => usuarios.find((u) => String(u.id) === String(id))?.nome || "—";
  const etapaFinal = etapasAtivas[etapasAtivas.length - 1];

  const alteracoesOrdenadas = useMemo(
    () => [...alteracoes].sort((a, b) => nomeEmpresa(a.empresaId).localeCompare(nomeEmpresa(b.empresaId), "pt-BR") || b.numero - a.numero),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alteracoes, empresas],
  );

  const alteracoesFiltradas = useMemo(() => {
    const termo = normalizar(filtro);
    if (!termo) return alteracoesOrdenadas;
    return alteracoesOrdenadas.filter((a) =>
      [nomeEmpresa(a.empresaId), a.numero, a.identificacao, nomeEtapa(a.etapaId), nomeUsuario(a.responsavelId)].some((v) => normalizar(v).includes(termo)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alteracoesOrdenadas, filtro]);

  const alteracoesPagina = alteracoesFiltradas.slice(pagina * porPagina, pagina * porPagina + porPagina);

  // Alteração montada a partir do formulário (usada nos cálculos).
  const registroEditado = alteracoes.find((a) => a.id === editandoId) || null;
  const outrasDaEmpresa = form.empresaId ? alteracoesDaEmpresa(alteracoes, form.empresaId).filter((a) => a.id !== editandoId) : [];
  const cargaInicial = Boolean(form.empresaId) && outrasDaEmpresa.length === 0;

  // Número sequencial (seção 3): automático = última alteração da empresa + 1.
  // Só a carga inicial (primeira alteração lançada da empresa) tem o número
  // informado, pois o histórico pode começar em qualquer alteração (ex.: 17ª).
  const numeroAutomatico = Boolean(form.empresaId) && !cargaInicial;
  const ultimaDaEmpresa = outrasDaEmpresa[outrasDaEmpresa.length - 1];
  const numeroEfetivo = numeroAutomatico
    ? registroEditado
      ? registroEditado.numero
      : ultimaDaEmpresa.numero + 1
    : form.numero === ""
      ? NaN
      : Number(form.numero);

  const candidato = {
    id: editandoId ?? "__novo__",
    empresaId: form.empresaId,
    numero: numeroEfetivo,
    cargaInicial,
    posicaoInicial: form.posicaoInicial,
    movimentos: form.movimentos,
  };

  const ehUltimaDaEmpresa = (alteracao) => {
    const lista = alteracoesDaEmpresa(alteracoes, alteracao.empresaId);
    return lista[lista.length - 1]?.id === alteracao.id;
  };

  // Regras de edição: somente a última alteração da empresa (posições
  // encadeadas) e fora da etapa final — exceto lançamentos históricos.
  const motivoBloqueioEdicao = (alteracao) => {
    if (!alteracao) return "";
    if (!ehUltimaDaEmpresa(alteracao)) return "Somente a última alteração da empresa pode ser editada (as posições seguintes dependem dela).";
    if (alteracao.etapaId === etapaFinal?.id && !alteracao.lancamentoHistorico) return `Alteração já na etapa final (${etapaFinal.nome}).`;
    return "";
  };

  const situacaoAvanco = (() => {
    if (!registroEditado) return { permitido: false, motivo: "" };
    const indice = etapasAtivas.findIndex((e) => e.id === registroEditado.etapaId);
    const atual = etapasAtivas[indice];
    if (!atual || !etapasAtivas[indice + 1]) return { permitido: false, motivo: "" };
    if (!somenteLeitura) return { permitido: false, motivo: "Salve ou cancele a edição antes de avançar." };
    if (!atual.usuarios.some((u) => String(u.id) === String(usuarioLogadoId))) {
      return { permitido: false, motivo: `Somente os responsáveis pela etapa ${atual.nome} podem avançar.` };
    }
    if (!(registroEditado.documentos || []).some((d) => d.etapaId === atual.id)) {
      return { permitido: false, motivo: `Anexe ao menos um documento na etapa ${atual.nome} (aba Documentos).` };
    }
    return { permitido: true, motivo: "" };
  })();

  const handleLimparFiltro = () => {
    setInputFiltro("");
    setFiltro("");
    setPagina(0);
  };

  const handleCopiar = async () => {
    const texto = alteracoesFiltradas.map((a) => `${nomeEmpresa(a.empresaId)} - ${a.numero}ª - ${nomeEtapa(a.etapaId)}`).join(", ");
    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }
    const ok = await copiarTexto(texto);
    alert(ok ? "Copiado!" : "Não foi possível copiar.");
  };

  const handleCSV = () => {
    const BOM = "﻿";
    const header = ["Empresa", "Nº", "Identificação", "Etapa", "Responsável", "Assinatura", "Registro Junta", "Atualizado em"];
    const csvEscape = (value) => {
      const texto = String(value ?? "");
      return /[;"\n\r]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
    };
    const linhas = alteracoesFiltradas.map((a) =>
      [
        nomeEmpresa(a.empresaId),
        a.numero,
        a.identificacao,
        nomeEtapa(a.etapaId),
        nomeUsuario(a.responsavelId),
        data(a.dataAssinatura),
        data(a.dataRegistroJunta),
        dataHora(a.atualizadoEm),
      ].map(csvEscape),
    );
    const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");
    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "alteracoes-contratuais.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const preencherForm = (a) =>
    setForm({
      empresaId: String(a.empresaId),
      numero: String(a.numero),
      identificacao: a.identificacao || "",
      dataAssinatura: a.dataAssinatura || "",
      dataRegistroJunta: a.dataRegistroJunta || "",
      responsavelId: a.responsavelId,
      observacoes: a.observacoes || "",
      lancamentoHistorico: Boolean(a.lancamentoHistorico),
      posicaoInicial: (a.posicaoInicial || []).map((p) => ({ ...p })),
      movimentos: (a.movimentos || []).map((m) => ({ ...m })),
      documentos: (a.documentos || []).map((d) => ({ ...d })),
    });

  const handleVisualizar = (alteracao) => {
    setEditandoId(alteracao.id);
    setSomenteLeitura(true);
    preencherForm(alteracao);
    setErrosValidacao([]);
    setAbaAtiva("geral");
    setGridMinimizado(true);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const handleHabilitarEdicao = () => {
    const motivo = motivoBloqueioEdicao(registroEditado);
    if (motivo) return mostrarSnack(motivo, "warning");
    setSomenteLeitura(false);
  };

  const handleCancelarForm = () => {
    setEditandoId(null);
    setSomenteLeitura(false);
    setForm({ ...formVazio, responsavelId: usuarioLogadoId });
    setErrosValidacao([]);
    setAbaAtiva("geral");
    setGridMinimizado(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Ao cancelar a edição de um registro salvo, volta aos dados gravados.
  const handleCancelarEdicao = () => {
    if (!registroEditado) return handleCancelarForm();
    preencherForm(registroEditado);
    setSomenteLeitura(true);
    setErrosValidacao([]);
  };

  const atualizarCampo = (campo, valor) => {
    if (somenteLeitura) return;
    setForm((p) => ({ ...p, [campo]: valor }));
    if (errosValidacao.length) setErrosValidacao([]);
  };

  const selecionarEmpresa = (empresaId) => {
    if (somenteLeitura || editandoId) return;
    const lista = alteracoesDaEmpresa(alteracoes, empresaId);
    const ultima = lista[lista.length - 1];
    setForm((p) => ({ ...p, empresaId, numero: ultima ? String(ultima.numero + 1) : "", posicaoInicial: lista.length ? [] : p.posicaoInicial }));
  };

  const recarregarAlteracoes = async () => {
    const lista = await buscarLista("/alteracoes-contratuais/processos");
    setAlteracoes(lista);
    return lista;
  };

  // ── Salvar (criar ou editar) no banco
  const handleSalvar = async () => {
    if (processando) return;
    const erros = validarAlteracao(candidato, alteracoes);
    const responsavelId = form.responsavelId ?? usuarioLogadoId;
    if (!responsavelId) erros.push("Informe o responsável pela alteração.");
    if (!etapasAtivas.length) erros.push("Não há etapas ativas em Etapas do Fluxo.");
    // Encadeamento: só lança a próxima depois que a anterior chegou à etapa final.
    if (!editandoId && ultimaDaEmpresa && etapaFinal && ultimaDaEmpresa.etapaId !== etapaFinal.id) {
      erros.push(
        `A alteração nº ${ultimaDaEmpresa.numero} desta empresa ainda está em andamento (${nomeEtapa(ultimaDaEmpresa.etapaId)}). Conclua-a antes de lançar a próxima.`,
      );
    }
    if (form.lancamentoHistorico && (!form.dataAssinatura || !form.dataRegistroJunta)) {
      erros.push("Lançamento histórico (já registrado): informe a data de assinatura e a data de registro na Junta.");
    }
    if (form.dataAssinatura && form.dataRegistroJunta && form.dataRegistroJunta < form.dataAssinatura) {
      erros.push("A data de registro na Junta não pode ser anterior à data de assinatura.");
    }
    if (erros.length) {
      setErrosValidacao(erros);
      mostrarSnack("Corrija os pontos indicados antes de salvar.", "error");
      return;
    }
    setErrosValidacao([]);

    const corpo = {
      empresaId: Number(form.empresaId),
      numero: cargaInicial ? numeroEfetivo : undefined,
      identificacao: maiusculas(form.identificacao.trim()),
      dataAssinatura: form.dataAssinatura || null,
      dataRegistroJunta: form.dataRegistroJunta || null,
      responsavelId,
      observacoes: maiusculas(form.observacoes.trim()),
      lancamentoHistorico: form.lancamentoHistorico,
      posicaoInicial: cargaInicial ? form.posicaoInicial : [],
      movimentos: form.movimentos.map(({ socioId, tipo, direcao, quotas, valor }) => ({ socioId, tipo, direcao, quotas, valor })),
    };

    setProcessando(true);
    try {
      const salva = await chamarApi(editandoId ? `/${editandoId}` : "", { method: editandoId ? "PUT" : "POST", body: JSON.stringify(corpo) });
      const lista = await recarregarAlteracoes();
      const registro = lista.find((a) => a.id === salva.id);
      setEditandoId(salva.id);
      if (registro) preencherForm(registro);
      setSomenteLeitura(true);
      mostrarSnack(editandoId ? "Alteração atualizada!" : "Alteração cadastrada com sucesso!");
    } catch (erro) {
      if (erro.detalhes?.length) setErrosValidacao(erro.detalhes);
      mostrarSnack(erro.message, "error");
    } finally {
      setProcessando(false);
    }
  };

  // ── Avançar etapa (regra 5) — o servidor confere responsável e documento
  const handleAvancar = async () => {
    if (!situacaoAvanco.permitido || !registroEditado || processando) return;
    setProcessando(true);
    try {
      const r = await chamarApi(`/${registroEditado.id}/avancar`, { method: "POST" });
      await recarregarAlteracoes();
      mostrarSnack(r.mensagem || "Alteração avançada.");
    } catch (erro) {
      mostrarSnack(erro.message, "error");
    } finally {
      setProcessando(false);
    }
  };

  // ── Excluir (administrador; somente a última da empresa)
  const handleConfirmarDelete = (alteracao) => {
    if (!ehUltimaDaEmpresa(alteracao)) return mostrarSnack("Somente a última alteração da empresa pode ser excluída.", "warning");
    document.activeElement?.blur();
    setConfirmDelete({ aberto: true, id: alteracao.id });
  };

  const handleExcluirConfirmado = async () => {
    const { id } = confirmDelete;
    setConfirmDelete({ aberto: false, id: null });
    try {
      await chamarApi(`/${id}`, { method: "DELETE" });
      if (editandoId === id) handleCancelarForm();
      await recarregarAlteracoes();
      mostrarSnack("Alteração excluída.", "warning");
    } catch (erro) {
      mostrarSnack(erro.message, "error");
    }
  };

  // ── Documentos (GED): gravados direto no servidor; exigem alteração salva.
  const anexarDocumento = async (tipo, arquivo) => {
    const dados = new FormData();
    dados.append("tipo", tipo);
    dados.append("arquivo", arquivo);
    await chamarApi(`/${registroEditado.id}/documentos`, { method: "POST", body: dados });
    await recarregarAlteracoes();
    mostrarSnack("Documento anexado.");
  };

  const removerDocumento = async (documento) => {
    await chamarApi(`/${registroEditado.id}/documentos/${documento.id}`, { method: "DELETE" });
    await recarregarAlteracoes();
    mostrarSnack("Documento removido.", "warning");
  };

  const visualizarDocumento = async (documento) => {
    const tk = localStorage.getItem("token");
    const resposta = await fetch(`${API_PROCESSOS}/${registroEditado.id}/documentos/${documento.id}/arquivo`, { headers: { Authorization: `Bearer ${tk}` } });
    if (!resposta.ok) {
      const dados = await resposta.json().catch(() => ({}));
      throw new Error(dados.erro || "Não foi possível abrir o documento.");
    }
    const url = URL.createObjectURL(await resposta.blob());
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const documentosRegistro = registroEditado?.documentos || [];
  const podeAnexarDocumento = podeCadastrar && Boolean(registroEditado) && registroEditado.etapaId !== etapaFinal?.id;

  if (!podeVisualizar) {
    return (
      <ThemeProvider theme={muiTheme}>
        <Box sx={{ color: "var(--ig-t-ffffff)" }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Alterações Contratuais
          </Typography>
          <Alert severity="warning">Você não possui permissão para acessar esta tela.</Alert>
        </Box>
      </ThemeProvider>
    );
  }

  const motivoBloqueio = motivoBloqueioEdicao(registroEditado);
  const etapaDoForm = registroEditado ? registroEditado.etapaId : form.lancamentoHistorico ? etapaFinal?.id : etapasAtivas[0]?.id;

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "var(--ig-t-ffffff)" }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Alterações Contratuais
        </Typography>

        {/* ── Filtro ─────────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Filtrar Alteração"
            value={inputFiltro}
            onChange={(e) => setInputFiltro(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setFiltro(inputFiltro);
                setPagina(0);
              }
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "var(--ig-t-9ca3af)", fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": { backgroundColor: "transparent", color: "var(--ig-t-ffffff)" },
              "& input::placeholder": { color: "var(--ig-t-ffffff)", opacity: 1, fontWeight: 500, fontSize: "1rem" },
            }}
          />
          <Button
            size="small"
            variant="outlined"
            type="button"
            onClick={handleLimparFiltro}
            sx={{
              flexShrink: 0,
              width: "auto",
              height: 40,
              color: "#2196f3",
              borderColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": { borderColor: "#42a5f5", backgroundColor: "rgba(33, 150, 243, 0.08)" },
            }}
          >
            Limpar Filtro
          </Button>
          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCopiar}
            sx={{ height: 40, backgroundColor: "#2196f3", fontWeight: 600, textTransform: "none", "&:hover": { backgroundColor: "#1976d2" } }}
          >
            <i className="fas fa-copy" style={{ marginRight: 6 }} />
            Copiar
          </Button>
          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCSV}
            sx={{ height: 40, backgroundColor: "#2196f3", fontWeight: 600, textTransform: "none", "&:hover": { backgroundColor: "#1976d2" } }}
          >
            CSV
          </Button>
        </Box>

        {/* ── Minimizar grid ───────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 1 }}>
          {!gridMinimizado ? (
            <button
              type="button"
              onClick={() => setGridMinimizado(true)}
              style={{
                border: "1px solid #d32f2f",
                color: "#ff4c4c",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setGridMinimizado(false)}
              style={{
                border: "1px solid #1998a8",
                color: "#1998a8",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↗ Expandir
            </button>
          )}
        </Box>

        {/* ── Grid ─────────────────────────────────────────────────────────── */}
        <Collapse in={!gridMinimizado}>
          <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: "transparent", boxShadow: "none" }}>
            <Table size="small" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={estiloCabecalho}>Ações</TableCell>
                  <TableCell sx={estiloCabecalho}>Empresa</TableCell>
                  <TableCell align="center" sx={estiloCabecalho}>
                    Nº
                  </TableCell>
                  <TableCell sx={{ ...estiloCabecalho, width: "100%" }}>Identificação</TableCell>
                  <TableCell sx={estiloCabecalho}>Etapa</TableCell>
                  <TableCell sx={estiloCabecalho}>Responsável</TableCell>
                  <TableCell sx={estiloCabecalho}>Assinatura</TableCell>
                  <TableCell sx={estiloCabecalho}>Registro Junta</TableCell>
                  <TableCell sx={estiloCabecalho}>Atualizado em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alteracoesPagina.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ color: "var(--ig-t-888888)", py: 3, borderBottom: "1px solid var(--ig-b-444444)" }}>
                      {carregando ? "Carregando cadastros..." : "Nenhuma alteração contratual encontrada."}
                    </TableCell>
                  </TableRow>
                ) : (
                  alteracoesPagina.map((a) => {
                    const final = a.etapaId === etapaFinal?.id;
                    return (
                      <TableRow
                        key={a.id}
                        sx={{
                          "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" },
                          backgroundColor: editandoId === a.id ? "rgba(25,152,168,0.08)" : "transparent",
                        }}
                      >
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                          <Tooltip title="Visualizar / Editar">
                            <IconButton size="small" onClick={() => handleVisualizar(a)} sx={estiloBotaoAcao("#1998a8", "#1565c0")}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {podeExcluir && (
                            <Tooltip title={ehUltimaDaEmpresa(a) ? "Excluir alteração" : "Somente a última alteração da empresa pode ser excluída"}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!ehUltimaDaEmpresa(a)}
                                  onClick={() => handleConfirmarDelete(a)}
                                  sx={estiloBotaoAcao("#d32f2f", "#c62828")}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)", whiteSpace: "nowrap" }}>
                          {nomeEmpresa(a.empresaId)}
                        </TableCell>
                        <TableCell align="center" sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)", whiteSpace: "nowrap" }}>
                          {a.numero === 0 ? "—" : `${a.numero}ª`}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)" }}>
                          {a.identificacao || (a.numero === 0 ? "CONSTITUIÇÃO" : `${a.numero}ª ALTERAÇÃO CONTRATUAL`)}
                          {a.lancamentoHistorico && <Chip size="small" label="Histórico" sx={{ ml: 1, backgroundColor: "#555", color: "#fff" }} />}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                          <Chip
                            size="small"
                            label={nomeEtapa(a.etapaId)}
                            sx={{ backgroundColor: final ? "#2e7d32" : "#1998a8", color: "#fff", fontWeight: "bold" }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>
                          {nomeUsuario(a.responsavelId)}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>
                          {data(a.dataAssinatura)}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>
                          {data(a.dataRegistroJunta)}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>
                          {dataHora(a.atualizadoEm)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={alteracoesFiltradas.length}
              page={pagina}
              onPageChange={(_, p) => setPagina(p)}
              rowsPerPage={porPagina}
              onRowsPerPageChange={(e) => {
                setPorPagina(parseInt(e.target.value, 10));
                setPagina(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Alterações por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{
                color: "var(--ig-t-ffffff)",
                borderTop: "1px solid var(--ig-b-444444)",
                "& .MuiTablePagination-selectIcon": { color: "var(--ig-t-ffffff)" },
                "& .MuiTablePagination-actions button": { color: "var(--ig-t-ffffff)", width: "auto" },
                "& .MuiTablePagination-actions": { display: "flex", alignItems: "center" },
              }}
            />
          </TableContainer>
        </Collapse>

        {/* ── Formulário inferior (visualizar / criar / editar) ────────────── */}
        {(podeCadastrar || (podeVisualizar && editandoId)) && (
          <Paper sx={{ p: 3, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, boxShadow: "0 0 20px rgba(0,0,0,0.4)" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Typography variant="h6" sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>
                {editandoId && somenteLeitura ? "Visualizar Alteração Contratual" : editandoId ? "Editar Alteração Contratual" : "Nova Alteração Contratual"}
              </Typography>
              {etapaDoForm && (
                <Chip size="small" label={`Etapa: ${nomeEtapa(etapaDoForm)}`} sx={{ backgroundColor: "#1998a8", color: "#fff", fontWeight: "bold" }} />
              )}
              {cargaInicial && <Chip size="small" label="Carga inicial da empresa" sx={{ backgroundColor: "#ed6c02", color: "#fff", fontWeight: "bold" }} />}
            </Box>

            {editandoId && somenteLeitura && motivoBloqueio && podeCadastrar && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {motivoBloqueio}
              </Alert>
            )}

            {errosValidacao.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <b>Não foi possível salvar:</b>
                <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                  {errosValidacao.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              </Alert>
            )}

            <Tabs
              value={abaAtiva}
              onChange={(_, v) => setAbaAtiva(v)}
              variant="scrollable"
              sx={{
                mb: 2,
                borderBottom: "1px solid var(--ig-b-333333)",
                "& .MuiTab-root": { color: "var(--ig-t-aaaaaa)", textTransform: "none", fontWeight: 600 },
                "& .MuiTab-root.Mui-selected": { color: "#1998a8" },
                "& .MuiTabs-indicator": { backgroundColor: "#1998a8" },
              }}
            >
              <Tab value="geral" label="Dados Gerais" />
              <Tab value="posicao" label="Posição Societária" />
              <Tab value="movimentos" label={`Movimentos (${form.movimentos.length})`} />
              <Tab value="documentos" label={`Documentos (${documentosRegistro.length})`} />
              <Tab value="fluxo" label="Fluxo" />
            </Tabs>

            {abaAtiva === "geral" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <FormControl size="small" sx={{ flex: 2, minWidth: 240 }}>
                    <InputLabel sx={{ color: "var(--ig-t-ffffff)", fontWeight: "bold" }}>Empresa*</InputLabel>
                    <Select
                      label="Empresa*"
                      value={form.empresaId}
                      onChange={(e) => selecionarEmpresa(e.target.value)}
                      inputProps={{ readOnly: somenteLeitura || Boolean(editandoId) }}
                      sx={estiloSelect(somenteLeitura || Boolean(editandoId), false)}
                    >
                      {empresas
                        .filter((e) => e.ativa || String(e.id) === String(form.empresaId))
                        .map((e) => (
                          <MenuItem key={e.id} value={String(e.id)}>
                            {e.apelido} — {e.razaoSocial}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Nº da Alteração*"
                    size="small"
                    type="number"
                    value={numeroAutomatico ? String(numeroEfetivo) : form.numero}
                    onChange={(e) => !numeroAutomatico && form.empresaId && atualizarCampo("numero", e.target.value)}
                    helperText={
                      !form.empresaId
                        ? "Selecione a empresa"
                        : numeroAutomatico
                          ? "Sequencial automático"
                          : "Carga inicial: nº da 1ª alteração lançada (0 = constituição)"
                    }
                    slotProps={{ input: { readOnly: somenteLeitura || numeroAutomatico || !form.empresaId }, htmlInput: { min: 0 } }}
                    sx={{ flex: 1, minWidth: 180, ...estiloCampo(somenteLeitura || numeroAutomatico || !form.empresaId, false) }}
                  />

                  <TextField
                    label="Identificação"
                    size="small"
                    value={form.identificacao}
                    onChange={(e) => atualizarCampo("identificacao", maiusculas(e.target.value))}
                    helperText="Opcional. Ex.: 17ª/18ª ALTERAÇÃO, CONSTITUIÇÃO"
                    slotProps={{ input: { readOnly: somenteLeitura }, htmlInput: { maxLength: 100 } }}
                    sx={{ flex: 2, minWidth: 240, ...estiloCampo(somenteLeitura, false) }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField
                    label="Data de Assinatura"
                    size="small"
                    type="date"
                    value={form.dataAssinatura}
                    onChange={(e) => atualizarCampo("dataAssinatura", e.target.value)}
                    slotProps={{ input: { readOnly: somenteLeitura }, inputLabel: { shrink: true } }}
                    sx={{
                      flex: 1,
                      minWidth: 180,
                      ...estiloCampo(somenteLeitura, false),
                      "& input::-webkit-calendar-picker-indicator": { filter: "var(--ig-x-11o99ka)" },
                    }}
                  />
                  <TextField
                    label="Data de Registro na Junta"
                    size="small"
                    type="date"
                    value={form.dataRegistroJunta}
                    onChange={(e) => atualizarCampo("dataRegistroJunta", e.target.value)}
                    slotProps={{ input: { readOnly: somenteLeitura }, inputLabel: { shrink: true } }}
                    sx={{
                      flex: 1,
                      minWidth: 180,
                      ...estiloCampo(somenteLeitura, false),
                      "& input::-webkit-calendar-picker-indicator": { filter: "var(--ig-x-11o99ka)" },
                    }}
                  />
                  <Autocomplete
                    size="small"
                    readOnly={somenteLeitura}
                    forcePopupIcon={!somenteLeitura}
                    options={usuarios}
                    getOptionLabel={(u) => u.nome}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={usuarios.find((u) => String(u.id) === String(form.responsavelId ?? usuarioLogadoId)) || null}
                    onChange={(_, val) => atualizarCampo("responsavelId", val?.id ?? null)}
                    renderInput={(params) => <TextField {...params} label="Responsável*" size="small" sx={estiloCampo(somenteLeitura, false)} />}
                    sx={{ flex: 2, minWidth: 240 }}
                  />
                </Box>

                {!editandoId && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.lancamentoHistorico}
                        onChange={(e) => atualizarCampo("lancamentoHistorico", e.target.checked)}
                        sx={{ color: "var(--ig-t-888888)", "&.Mui-checked": { color: "#1998a8" } }}
                      />
                    }
                    label={`Lançamento histórico (alteração já registrada — entra direto na etapa ${etapaFinal?.nome || "final"})`}
                    sx={{ color: "var(--ig-t-cccccc)" }}
                  />
                )}

                <TextField
                  label="Observações"
                  size="small"
                  multiline
                  minRows={2}
                  value={form.observacoes}
                  onChange={(e) => atualizarCampo("observacoes", maiusculas(e.target.value))}
                  slotProps={{ input: { readOnly: somenteLeitura } }}
                  sx={estiloCampo(somenteLeitura, false)}
                />
              </Box>
            )}

            {abaAtiva === "posicao" &&
              (form.empresaId ? (
                <SocietarioPosicao
                  alteracao={candidato}
                  alteracoes={alteracoes}
                  socios={socios}
                  somenteLeitura={somenteLeitura}
                  onChangePosicaoInicial={(lista) => atualizarCampo("posicaoInicial", lista)}
                />
              ) : (
                <Alert severity="warning">Selecione a empresa na aba Dados Gerais.</Alert>
              ))}

            {abaAtiva === "movimentos" && (
              <SocietarioMovimentos
                movimentos={form.movimentos}
                socios={socios}
                somenteLeitura={somenteLeitura}
                onChange={(lista) => atualizarCampo("movimentos", lista)}
              />
            )}

            {abaAtiva === "documentos" && (
              <SocietarioDocumentos
                documentos={documentosRegistro}
                alteracaoSalva={Boolean(registroEditado)}
                podeAnexar={podeAnexarDocumento}
                etapaAtual={etapaDoForm}
                nomeEtapa={nomeEtapa}
                onAnexar={anexarDocumento}
                onRemover={removerDocumento}
                onVisualizar={visualizarDocumento}
              />
            )}

            {abaAtiva === "fluxo" && (
              <SocietarioFluxo
                alteracao={registroEditado || { etapaId: etapaDoForm, historico: [] }}
                etapasAtivas={etapasAtivas}
                nomeEtapa={nomeEtapa}
                situacaoAvanco={situacaoAvanco}
                onAvancar={handleAvancar}
                alteracaoSalva={Boolean(registroEditado)}
              />
            )}

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 3 }}>
              {editandoId && somenteLeitura && (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleCancelarForm}
                    sx={{
                      width: "auto",
                      borderColor: "var(--ig-b-555555)",
                      color: "var(--ig-t-cccccc)",
                      "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                      fontWeight: "bold",
                      px: 3,
                    }}
                  >
                    Fechar
                  </Button>
                  {podeCadastrar && (
                    <Button
                      variant="contained"
                      startIcon={<FaEdit />}
                      onClick={handleHabilitarEdicao}
                      disabled={Boolean(motivoBloqueio)}
                      sx={{ width: "auto", backgroundColor: "#1998a8", "&:hover": { backgroundColor: "#1565c0" }, fontWeight: "bold", py: 1, px: 3 }}
                    >
                      Editar
                    </Button>
                  )}
                  {podeExcluir && registroEditado && ehUltimaDaEmpresa(registroEditado) && (
                    <Button
                      variant="contained"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleConfirmarDelete(registroEditado)}
                      sx={{ width: "auto", backgroundColor: "#d32f2f", "&:hover": { backgroundColor: "#c62828" }, fontWeight: "bold", py: 1, px: 3 }}
                    >
                      Excluir
                    </Button>
                  )}
                </>
              )}
              {(!editandoId || !somenteLeitura) && (
                <>
                  {editandoId && (
                    <Button
                      variant="outlined"
                      startIcon={<FaTimes />}
                      onClick={handleCancelarEdicao}
                      sx={{
                        width: "auto",
                        borderColor: "var(--ig-b-555555)",
                        color: "var(--ig-t-cccccc)",
                        "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                        fontWeight: "bold",
                        px: 3,
                      }}
                    >
                      Cancelar
                    </Button>
                  )}
                  {!editandoId && (form.empresaId || form.movimentos.length > 0) && (
                    <Button
                      variant="outlined"
                      startIcon={<FaTimes />}
                      onClick={handleCancelarForm}
                      sx={{
                        width: "auto",
                        borderColor: "var(--ig-b-555555)",
                        color: "var(--ig-t-cccccc)",
                        "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                        fontWeight: "bold",
                        px: 3,
                      }}
                    >
                      Limpar
                    </Button>
                  )}
                  {podeCadastrar && (
                    <Button
                      variant="contained"
                      startIcon={<FaSave />}
                      onClick={handleSalvar}
                      disabled={processando}
                      sx={{ width: "auto", backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" }, fontWeight: "bold", py: 1, px: 4 }}
                    >
                      {editandoId ? "Atualizar" : "Cadastrar"}
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Paper>
        )}
      </Box>

      {/* ── Dialog: Confirmação de exclusão ──────────────────────────────── */}
      <Dialog open={confirmDelete.aberto} onClose={() => setConfirmDelete({ aberto: false, id: null })}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "var(--ig-t-cccccc)" }}>
            Deseja excluir esta alteração contratual, com seus movimentos e documentos? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmDelete({ aberto: false, id: null })}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleExcluirConfirmado}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.aberto}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, aberto: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, aberto: false }))} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
